import chaptersJson from "@/assets/data/chapters.json";
import libraryJson from "@/assets/data/library.json";
import courseTemplateJson from "@/assets/data/course-template.json";
import stillYourselfJson from "@/assets/data/still-yourself.json";

export type DocRegistryRecord = {
  docId: number;
  slug: string;
  contentFile: string;
};

function isFiniteNumber(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

export function isNumericRouteParam(v: unknown): v is string {
  return typeof v === "string" && /^\d+$/.test(v);
}

function normalizeSlug(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const s = v.trim();
  return s ? s : null;
}

function normalizeContentFile(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const s = v.trim();
  if (!s) return null;
  // Safety: avoid path traversal; only allow filenames.
  if (s.includes("/") || s.includes("\\")) return null;
  if (!s.toLowerCase().endsWith(".md")) return null;
  return s;
}

function collectRecords(): DocRegistryRecord[] {
  const out: DocRegistryRecord[] = [];
  const add = (candidate: any) => {
    // Entries flagged published:false or private:true must not resolve to a
    // route at all. These flags used to gate feed visibility only, which meant
    // an unpublished doc was still reachable and prerendered by slug.
    if (candidate?.published === false || candidate?.private === true) return;

    const docId = candidate?.docId;
    const slug = normalizeSlug(candidate?.slug);
    const contentFile = normalizeContentFile(candidate?.contentFile);

    if (!isFiniteNumber(docId) || !slug || !contentFile) return;
    out.push({ docId, slug, contentFile });
  };

  const chapterEntries = (chaptersJson as any)?.entries || [];
  const libraryEntries = (libraryJson as any)?.entries || [];
  const courseTemplateEntries = (courseTemplateJson as any)?.entries || [];
  const stillYourselfEntries = (stillYourselfJson as any)?.entries || [];

  for (const e of chapterEntries) add(e);
  for (const e of libraryEntries) add(e);
  for (const e of courseTemplateEntries) add(e);
  for (const e of stillYourselfEntries) add(e);

  return out;
}

const records = collectRecords();

const byId = new Map<number, DocRegistryRecord>();
const bySlug = new Map<string, DocRegistryRecord>();

for (const r of records) {
  if (!byId.has(r.docId)) byId.set(r.docId, r);
  if (!bySlug.has(r.slug)) bySlug.set(r.slug, r);
}

export function getDocRecordById(docId: number): DocRegistryRecord | undefined {
  return byId.get(docId);
}

export function getDocRecordBySlug(slug: string): DocRegistryRecord | undefined {
  return bySlug.get(slug);
}

// The title a doc's Library entry gives it, for the breadcrumb, so a crumb
// reads "Token pipeline for a multi-brand platform" rather than a title-cased
// slug. Undefined when the doc has no Library entry.
export function getDocTitle(slug: string): string | undefined {
  if (!slug) return undefined;
  const entry = ((libraryJson as any)?.entries || []).find((e: any) => e?.slug === slug);
  return typeof entry?.title === "string" && entry.title.trim() ? entry.title.trim() : undefined;
}
